import { BadgeNumber, LegacyLayout } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Icon, IconType } from '@components/icon.component';
import { VisibaLink } from '@components/visiba-link.component';
import { rem } from '@visiba/std';
import classNames from 'classnames';
import type { MouseEvent, ReactNode } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { useRouteDetails } from './_use_route_details';

interface Props {
	icon: IconType;
	iconActive: IconType;
	iconSize?: number;
	to: string;
	label?: ReactNode;
	onClick(event: MouseEvent<HTMLAnchorElement>): void;
	badgeNumber?: number;
	disabled?: boolean;
	__TEMP__navigate?: (url: string) => void;
}

const elements = {
	link: styled(VisibaLink)<{ isCurrentPage: boolean; disabled?: boolean }>`
		display: flex;
		justify-content: center;
		align-items: center;
		height: 100%;
		flex: 1;
		margin: 0 ${rem(10)};
		font-size: 1rem;
		line-height: 1.256875rem;
		letter-spacing: 0.025625rem;
		font-weight: 400;
		font-variant: small-caps;
		text-transform: lowercase;

		color: ${tokens.color.content.mutedBrand};

		&.isCurrentPage {
			color: ${tokens.color.content.brand};
		}

		transition:
			background 200ms ease-in-out,
			color 200ms ease;

		${({ disabled }) =>
			!disabled &&
			`
				&:hover, &:focus {
					background: ${tokens.color.bg.surfaceBrand};
					color: ${tokens.color.content.mutedBrand};
				}
			`}

		${({ disabled }) => disabled && `opacity: 0.5;`}

		&.no-label {
			padding: ${rem(8)} 0;
			border-radius: ${rem(8)};
		}

		.label {
			margin-left: ${rem(6)};
			text-overflow: ellipsis;
			max-width: 300px;
			white-space: nowrap;
			overflow: hidden;
		}
	`,
	icon: styled.div<{ size: number }>(
		({ size }) => `
			position: relative;
			width: ${rem(size)};
			height: ${rem(size)};
			justify-content: center;
			align-items: center;
			flex-shrink: 0;

			svg {
				font-size: ${rem(size)};
			}

			.badge {
				position: absolute;
				left: 60%;
				bottom: 60%;
			}
	`,
	),
};

export function Item({
	icon,
	iconActive,
	iconSize,
	to: _to,
	label,
	badgeNumber,
	disabled,
	__TEMP__navigate,
	...rest
}: Props): JSX.Element {
	const [isHovering, setIsHovering] = useState<boolean>(false);

	const [to, isCurrentPage] = useRouteDetails(_to);

	return (
		<elements.link
			to={to}
			isCurrentPage={isCurrentPage}
			disabled={disabled}
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
			__TEMP__to={to}
			__TEMP__navigate={__TEMP__navigate}
			className={classNames({ 'no-label': !label, isCurrentPage })}
			{...rest}
		>
			<LegacyLayout padding={`0 ${rem(8)}`} width={[null]} height={[null]} direction='row' alignment='center'>
				<elements.icon size={iconSize ?? 32}>
					{badgeNumber != null && badgeNumber > 0 ? (
						<div className='badge'>
							<BadgeNumber decorative value={badgeNumber} clampMax={99} />
						</div>
					) : null}

					<Icon type={(isCurrentPage || isHovering) && !disabled ? iconActive : icon} />
				</elements.icon>
				{label && <span className='label'>{label}</span>}
			</LegacyLayout>
		</elements.link>
	);
}
